import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { resourceTypesAPI } from '../services/api';
import { nikaoColors } from '../theme/nikaoTheme';
import toast from 'react-hot-toast';

const ResourceTypes = () => {
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResourceType, setEditingResourceType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    utilization_rate: 0.9
  });

  useEffect(() => {
    loadResourceTypes();
  }, []);

  const loadResourceTypes = async () => {
    try {
      setLoading(true);
      const response = await resourceTypesAPI.getAll();
      setResourceTypes(response.data);
    } catch (error) {
      console.error('Error loading resource types:', error);
      toast.error('Failed to load resource types');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Resource type name is required');
      return;
    }

    try {
      if (editingResourceType) {
        await resourceTypesAPI.update(editingResourceType.id, formData);
        toast.success('Resource type updated successfully');
      } else {
        await resourceTypesAPI.create(formData);
        toast.success('Resource type created successfully');
      }
      
      setShowModal(false);
      setEditingResourceType(null);
      setFormData({ name: '', description: '', utilization_rate: 0.9 });
      loadResourceTypes();
    } catch (error) {
      console.error('Error saving resource type:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save resource type';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (resourceType) => {
    setEditingResourceType(resourceType);
    setFormData({
      name: resourceType.name || '',
      description: resourceType.description || '',
      utilization_rate: resourceType.utilization_rate !== undefined ? parseFloat(resourceType.utilization_rate) : 0.9
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource type?')) {
      return;
    }

    try {
      await resourceTypesAPI.delete(id);
      toast.success('Resource type deleted successfully');
      loadResourceTypes();
    } catch (error) {
      console.error('Error deleting resource type:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete resource type';
      toast.error(errorMessage);
    }
  };

  const openCreateModal = () => {
    setEditingResourceType(null);
    setFormData({ name: '', description: '', utilization_rate: 0.9 });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingResourceType(null);
    setFormData({ name: '', description: '', utilization_rate: 0.9 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading resource types...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold" style={{ color: nikaoColors.text }}>
          Resource Types
        </h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
          style={{ backgroundColor: nikaoColors.primary }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Resource Type
        </button>
      </div>


      {/* Resource Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resourceTypes.map((resourceType) => (
          <div
            key={resourceType.id}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: nikaoColors.text }}>
                  {resourceType.name}
                </h3>
                {resourceType.description && (
                  <p className="text-sm mt-1" style={{ color: nikaoColors.textSecondary }}>
                    {resourceType.description}
                  </p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(resourceType)}
                  className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(resourceType.id)}
                  className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center text-sm" style={{ color: nikaoColors.textSecondary }}>
                <Users className="w-4 h-4 mr-2" />
                {resourceType.resources_count || 0} resources
              </div>
              <div className="flex items-center text-sm" style={{ color: nikaoColors.textSecondary }}>
                <span className="font-medium">Utilization Rate:</span>
                <span className="ml-2">
                  {((resourceType.utilization_rate || 0.9) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {resourceTypes.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto mb-4" style={{ color: nikaoColors.textSecondary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: nikaoColors.text }}>
            No resource types found
          </h3>
          <p className="text-sm" style={{ color: nikaoColors.textSecondary }}>
            Get started by creating your first resource type.
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4" style={{ color: nikaoColors.text }}>
              {editingResourceType ? 'Edit Resource Type' : 'Add Resource Type'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter resource type name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description (optional)"
                  rows="3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Utilization Rate (Default: 0.9 = 90%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formData.utilization_rate}
                  onChange={(e) => setFormData({ ...formData, utilization_rate: parseFloat(e.target.value) || 0.9 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.9"
                  title="Utilization rate accounting for sickness and annual leave (0.9 = 90%)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard rate: 0.9 (90%). This affects all SOW entries using this resource type.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: nikaoColors.primary }}
                >
                  {editingResourceType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceTypes;
